// @vitest-environment node
import { HttpResponse, graphql } from 'msw';
import { CustomerType, VerificationType } from '@brighte/validation';
import { faker } from '@faker-js/faker';
import {
  AuthenticatePasswordlessUserByEmailInput,
  AuthenticatePasswordlessUserByEmailMutation,
  AuthenticatePasswordlessUserBySMSInput,
  AuthenticatePasswordlessUserBySMSMutation,
  CreatePasswordlessUserInput,
  CreatePasswordlessUserMutation,
  QuotePrepopValuesByTrackingIdQuery,
  QuoteStatus,
  SendEmailVerificationMutation,
  SendEmailVerificationMutationVariables,
  SendSmsVerificationMutation,
  SendSmsVerificationMutationVariables,
  VerifyUpdatedUserEmailMutation,
  VerifyUpdatedUserEmailMutationVariables,
  VerifyUpdatedUserMobileMutation,
  VerifyUpdatedUserMobileMutationVariables,
} from '../graphql';
import { accessToken, idToken, mockAuthError, mockVerifyUpdate } from './mocks';
import { getMockJWTToken } from './token';

export const handlers = [
  graphql.mutation<CreatePasswordlessUserMutation, { input: CreatePasswordlessUserInput }>(
    'CreatePasswordlessUser',
    () => {
      const data = { createPasswordlessUser: '' };

      return HttpResponse.json({ data });
    },
  ),

  graphql.mutation<AuthenticatePasswordlessUserBySMSMutation, { input: AuthenticatePasswordlessUserBySMSInput }>(
    'AuthenticatePasswordlessUserBySMS',
    async ({ variables }) => {
      const mockToken = await getMockJWTToken(accessToken);
      const mockIdToken = await getMockJWTToken(idToken);

      const inputCode = variables?.input?.code;

      if (inputCode === '000000') {
        return HttpResponse.json({
          data: null,
          errors: mockAuthError('authenticatePasswordlessUserBySMS', 'INVALID_CODE'),
        });
      }

      if (inputCode === '000001') {
        return HttpResponse.json({
          data: null,
          errors: mockAuthError('authenticatePasswordlessUserBySMS', 'MAX_ATTEMPT'),
        });
      }

      const data = {
        authenticatePasswordlessUserBySMS: {
          accessToken: mockToken,
          idToken: mockIdToken,
          expiresIn: 10800,
          scope: 'openid profile phone email offline_access',
        },
      };

      return HttpResponse.json({ data });
    },
  ),

  graphql.mutation<AuthenticatePasswordlessUserByEmailMutation, { input: AuthenticatePasswordlessUserByEmailInput }>(
    'AuthenticatePasswordlessUserByEmail',
    async ({ variables }) => {
      const mockToken = await getMockJWTToken(accessToken);
      const mockIdToken = await getMockJWTToken(idToken);

      const inputCode = variables?.input?.code;

      if (inputCode === '000000') {
        return HttpResponse.json({
          data: null,
          errors: mockAuthError('authenticatePasswordlessUserByEmail', 'INVALID_CODE'),
        });
      }

      if (inputCode === '000001') {
        return HttpResponse.json({
          data: null,
          errors: mockAuthError('authenticatePasswordlessUserByEmail', 'MAX_ATTEMPT'),
        });
      }

      const data = {
        authenticatePasswordlessUserByEmail: {
          accessToken: mockToken,
          idToken: mockIdToken,
          expiresIn: 10800,
          scope: 'openid profile phone email offline_access',
        },
      };

      return HttpResponse.json({ data });
    },
  ),

  // Update Contact details API
  graphql.mutation<SendEmailVerificationMutation, { input: SendEmailVerificationMutationVariables }>(
    'SendEmailVerification',
    ({ variables }) => {
      const data = {
        sendEmailVerification: {
          email: variables.input?.email,
          status: 201,
          statusText: 'Created',
        },
      };

      return HttpResponse.json({ data });
    },
  ),

  graphql.mutation<SendSmsVerificationMutation, SendSmsVerificationMutationVariables>(
    'SendSmsVerification',
    ({ variables }) => {
      const data = {
        sendSmsVerification: {
          mobile: variables.mobile,
          status: 201,
          statusText: 'Created',
        },
      };

      return HttpResponse.json({ data });
    },
  ),

  graphql.mutation<VerifyUpdatedUserEmailMutation, VerifyUpdatedUserEmailMutationVariables>(
    'VerifyUpdatedUserEmail',
    ({ variables }) => {
      if (variables?.authCode === '000000') {
        return HttpResponse.json({
          data: null,
          errors: mockVerifyUpdate('verifyUpdatedUserMobile', 'VERIFICATION_ERROR_INCORRECT_CODE'),
        });
      }

      if (variables?.authCode === '000001') {
        return HttpResponse.json({
          data: null,
          errors: mockVerifyUpdate('verifyUpdatedUserMobile', 'VERIFICATION_ERROR_MAX_ATTEMPTS'),
        });
      }

      const data = {
        verifyUpdatedUserEmail: {
          email: variables.email,
          id: 'U123',
        },
      };

      return HttpResponse.json({ data });
    },
  ),

  graphql.mutation<VerifyUpdatedUserMobileMutation, VerifyUpdatedUserMobileMutationVariables>(
    'VerifyUpdatedUserMobile',
    ({ variables }) => {
      const data = {
        verifyUpdatedUserMobile: {
          mobile: variables.mobile,
          id: 'U123',
        },
      };

      if (variables?.authCode === '000000') {
        return HttpResponse.json({
          data: null,
          errors: mockVerifyUpdate('verifyUpdatedUserMobile', 'VERIFICATION_ERROR_INCORRECT_CODE'),
        });
      }

      if (variables?.authCode === '000001') {
        return HttpResponse.json({
          data: null,
          errors: mockVerifyUpdate('verifyUpdatedUserMobile', 'VERIFICATION_ERROR_MAX_ATTEMPTS'),
        });
      }

      return HttpResponse.json({ data });
    },
  ),

  graphql.query<QuotePrepopValuesByTrackingIdQuery>('QuotePrepopValuesByTrackingId', () => {
    const data = {
      quotePrepopValuesByTrackingId: {
        verificationType: VerificationType.SmsEmail,
        email: faker.internet.email(),
        phone: faker.helpers.replaceSymbols('041#######'),
        financialProduct: { id: 'name', name: 'name', customerType: CustomerType.Residential },
        status: QuoteStatus.SENT,
      },
    };

    return HttpResponse.json({ data });
  }),

  graphql.mutation<SendSmsVerificationMutation>('SendSmsVerification', async () => {
    const data = {
      sendSmsVerification: {
        mobile: faker.helpers.replaceSymbols('041#######'),
        status: 200,
        statusText: 'OK',
      },
    };

    return HttpResponse.json({ data });
  }),

  graphql.mutation<VerifyUpdatedUserMobileMutation>('VerifyUpdatedUserMobile', async () => {
    const data = {
      verifyUpdatedUserMobile: {
        mobile: faker.helpers.replaceSymbols('041#######'),
        id: '123',
      },
    };

    return HttpResponse.json({ data });
  }),

  graphql.mutation<SendEmailVerificationMutation>('SendEmailVerification', async () => {
    const data = {
      sendEmailVerification: {
        email: faker.internet.email(),
        status: 200,
        statusText: 'OK',
      },
    };

    return HttpResponse.json({ data });
  }),

  graphql.mutation<VerifyUpdatedUserEmailMutation>('VerifyUpdatedUserEmail', async () => {
    const data = {
      verifyUpdatedUserEmail: {
        email: faker.internet.email(),
        id: '123',
      },
    };

    return HttpResponse.json({ data });
  }),
];
